import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';

import { 
    CactusLabelInput, 
    DashboardTitle, 
    DashboardWrapper,
    SelectAsyncPaginate,
    ModalDeleteConfirm,
    DashboardScreenItem,
    CactusModal,
    DetailBreadcrumb
} from '../../components';

import HierarchyModel, { getTransferSubordinate, getHierarchyOption, OrganizationChartData, getOrganizationChartData } from "../../models/Hierarchy";
import * as actionCreator from "../../actions";

import LicenseService from '../../services/use_case/license/LicenseService';
import ApiResponse from '../../services/ApiResponse';
import { LicenseInputs } from '../Licenses/Licenses';
import License from '../../models/License';
import Screen from '../../models/Screen';

import Option from '../../models/Option';
import moment from 'moment';

interface LicenseDetailProps {};

const LicenseDetail: React.FC<LicenseDetailProps> = (props: LicenseDetailProps) => {

    const [t] = useTranslation();

    const dispatch = useDispatch();

    const _licenseService: LicenseService = new LicenseService();
    
    let { id: licenseId } = useParams<{id:string}>()
    const [isLoading, setisLoading] = useState(false)
    const [isUpdatingForm, setisUpdatingForm] = useState(false)
    const [transferLicense, settransferLicense] = useState(false)
    const [recoveringLicense, setrecoveringLicense] = useState(false)
    const [license, setlicense] = useState<License>()
    const [title, settitle] = useState<string>(t('License'))
    const [value, setValue] = useState<Option | null>(null);

    const hierarchyReducer = useSelector( (store: any) => store.hierarchyReducer );
    const { user } = useSelector( (store: any) => store.authReducer );
    const data: HierarchyModel | null = hierarchyReducer?.data;

    const [dataSource, setDataSource] = useState<OrganizationChartData | null>(null);
    const [subordinates, setSubordinates] = useState<Option[]>([]);

    let history = useHistory()
        
    const { handleSubmit, register, reset } = useForm<LicenseInputs & { id?: string; created_at?: Date }>({
        defaultValues: {
            ...license
        }
    });

    useEffect(() => {
        _fetchHierarchy(); 
    }, [])

    useEffect( () => {
        setDataSource( getOrganizationChartData(data) );
    }, [data]);

    useEffect( () => {
        if(dataSource) {
            const subordinates: HierarchyModel[] = getTransferSubordinate(dataSource.data);
            setSubordinates(subordinates.map((data: HierarchyModel) => getHierarchyOption(data)));
        }
    }, [dataSource]);
    
    useEffect(() => {
        _getLicense(licenseId) 
        return () => {
            _licenseService.cancelRequest()
        }
    }, [licenseId])
    
    useEffect(() => {
        if (license && subordinates.length > 0) {
            const matchingSon = subordinates.find((son: Option) => son.value === license.user_transfer_id);
            if (matchingSon) {
                setValue(matchingSon);
            } else if (license.user_transfer_id === user?.id) {
                setValue({
                    value: user?.id,
                    label: `${user.name ?? ""} ${user.last_name ?? ""} (${t('You')})`
                })
            }
        }
    }, [license, subordinates]);

    const _getLicense = async( id:string ) => {
        setisLoading(true)

        try {
            const licenseResponse:AxiosResponse<ApiResponse<License>> = await _licenseService.show(id);
            const license:License = licenseResponse.data.data!
            setisLoading(false)
            setlicense(license)
            const matchingSon = subordinates.find((son:Option) => son.value === license.user_transfer_id);
            if (matchingSon) {
                setValue(matchingSon);
            } else if (license.user_transfer_id === user?.id) {
                setValue({
                    value: user?.id,
                    label: `${user.name ?? ""} ${user.last_name ?? ""}`
                })
            }
            reset(license)
            settitle(t('License') + ': ' + (license?.name ?? license?.id))
        } catch (error) {
            history.replace('/licenses')
        }
    }

    const _fetchHierarchy = async() => {
        await dispatch( actionCreator.hierarchyFetch() );
    }

    const getHierarchyOptions = () => {
        const noOption: Option= {
            label: '-',
            value: null
        }
        return {
            options: [
                noOption, 
                ...subordinates
            ],
            hasMore: false,
        }
    }

    const disableSelect = () => {
        if(license?.resources || subordinates.length === 0) {
            return true
        }
        return false
    }

    const _renderTransferBtn = () => {
        let oldValue = license?.user_transfer_id
        if(value && value.value && value.value !== user?.id && value.value !== oldValue) {
            return (
                <button 
                    type="button"
                    onClick={ ()=> settransferLicense(true) }
                    className="btn btn-cactus mt-2 mr-2"
                    disabled={isUpdatingForm ?? false}
                >
                    {t('Transfer_license')}
                </button>
            )
        }
    }

    const _renderRecoverBtn = () => {
        if(license?.user_transfer_id && license?.user_transfer_id !== user?.id && !license?.resources) {
            return (
                <button 
                    type="button"
                    onClick={ () => setrecoveringLicense(true) }
                    className="btn btn-cactus--red mt-2"
                    disabled={isUpdatingForm ?? false}
                >
                    {t('Recover_license')}
                </button>
            )
        }
    }


    const _renderPlaceholder = () => {
        if(license?.resources) {
            return t('You have to unlink this license before you can tranfer it')
        }

        if(subordinates.length === 0){
            return t('You have no subordinates. Create a hierarchy first')
        }

        return "";
    }

    const _renderText = () => {
        if(license?.user_transfer_id === user?.id) {
            return (
                <>
                    <div className="col-12">
                        <p>{t('This_license_has_been_transferred_to_you. Are_you_sure_to_transfer_current_license_to_another_user?')}</p>
                    </div>
                </>
            )
        }

        return (
            <>
                <div className="col-12">
                    <p>{t('Are_you_sure_to_transfer_current_license_to_another_user?')}</p>
                </div>
            </>
        )
    }

    const _renderTransferSelect = () => {
        return (
            <>
                <div className="row">
                    <div className='col-sm-8' style={{marginTop: "-4px"}}>
                        <label className="w-100">
                            <span className="form__label">{t('Transfer License to User')}</span>
                        </label>
                        <SelectAsyncPaginate
                            fetchOptions={getHierarchyOptions}
                            value={value}
                            onChange={(selectedOption: Option) => {
                                setValue(selectedOption);
                            }}
                            disable={disableSelect()}
                            placeholder="-"
                        />
                    </div>
                    <div className="col-sm-4 d-flex align-items-center mt-3">
                        { _renderTransferBtn() }
                        { _renderRecoverBtn() }
                    </div>
                </div>
                <div className="row mt-2">
                    <div className="col-sm-8">
                        <label className="w-100">
                            <span>{_renderPlaceholder()}</span>
                        </label>
                    </div>
                </div>
            </>
        )
    }

    const renderResourceItem = () => {
        if(license?.resources) {
            return (
                <>
                    <label className="w-100">
                        <span className="form__label">{t('Associated_device')}</span>
                    </label>
                    
                    <div 
                        key={license.resources.id}
                        className="col-sm-4 col-lg-2 same-height mt-1 mb-1"
                    >
                        <Link to={`/screens/${license.resources.id}`} >
                            <DashboardScreenItem screen={license.resources as Screen} />
                        </Link>
                    </div>
                </>
            )
        }
    }
    
    const formatDate = (date: Date) => {
        return moment(date).format('YYYY-MM-DD HH:mm:ss');
    };

    const _handleOnSubmit = async () => {

        settransferLicense(false);

        if (!license || !value?.value) return;

        const formData = new FormData();
        formData.append("user_transfer_id", value.value);

        setisUpdatingForm(true)
        try {
            const transferResponse:AxiosResponse<ApiResponse<License>> = await _licenseService.transfer(licenseId, formData)
            setisUpdatingForm(false)
            setlicense(transferResponse.data.data!)
        } catch(error) {
            setisUpdatingForm(false)
        }
    };

    const _handleSaveName = async (data: LicenseInputs) => {
        setisUpdatingForm(true);
        try {
            const formData = new FormData();
            formData.append("name", data.name ?? "");
            const response: AxiosResponse<ApiResponse<License>> = await _licenseService.update(licenseId, formData);
            const updated = response.data.data!;
            setlicense(updated);
            settitle(t('License') + ': ' + (updated.name ?? updated.id));
        } catch (error) {
            // error handled by BaseService interceptor
        }
        setisUpdatingForm(false);
    };

    const _recoverLicense = async() => {
        setisUpdatingForm(true)
        try {
            const recoverResponse:AxiosResponse<ApiResponse<License>> = await _licenseService.recover(licenseId)
            setisUpdatingForm(false)
            setrecoveringLicense(false)
            setlicense(recoverResponse.data.data!)
            if (recoverResponse.data.data?.user_transfer) {
                let transferUser = recoverResponse.data.data?.user_transfer
                setValue({
                    value: transferUser.id,
                    label: `${transferUser.name ?? ""} ${transferUser.last_name ?? ""} (${t('You')})`
                })
            } else {
                setValue(null)
            }
            
        } catch (error) {
            setisUpdatingForm(false)
        }
    }

    const _renderLicenseForm = () => {
        return (
            <>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit(_handleOnSubmit)} >
                            <div className="row">
                                
                                <div className="col-sm-12">

                                    <div className="row">

                                        <div className="col-sm-6">
                                            <CactusLabelInput
                                                labelTitle={t('License_name')}
                                                inputAttributes={{
                                                    id: "name",
                                                    name: "name",
                                                    type: "text",
                                                    placeholder: t('License_name'),
                                                    autoComplete: "off",
                                                    ref: register
                                                }}
                                            />
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="d-flex justify-content-end align-items-center h-100">
                                                <button
                                                    type="button"
                                                    onClick={handleSubmit(_handleSaveName)}
                                                    className="btn btn-cactus"
                                                    disabled={isUpdatingForm}
                                                >
                                                    {t('Save')}
                                                </button>
                                            </div>
                                        </div>

                                    </div>

                                </div>

                                <div className="col-sm-4">
                                    <CactusLabelInput
                                        labelTitle={t('License_Id')}
                                        inputAttributes={{
                                            id: "id",
                                            name: "id",
                                            type:  "text",
                                            placeholder: t('License_Id'),
                                            autoComplete: "id",
                                            disabled: true,
                                            classprops: 'disabled',
                                            ref: register
                                        }}
                                    />
                                </div>

                                <div className="col-sm-2">
                                    <CactusLabelInput
                                        labelTitle={t('Created_at')}
                                        inputAttributes={{
                                            id: "created_at",
                                            name: "created_at",
                                            type:  "datetime",
                                            placeholder: 'dd/mm/yyyy',
                                            autoComplete: "Created_at",
                                            disabled: true,
                                            classprops: 'disabled',
                                            defaultValue: formatDate(license? license.created_at: new Date()),
                                        }}
                                    />
                                </div>

                                <div className="col-sm-6">
                                    { _renderTransferSelect() }
                                </div>
                            </div>
                            
                            <div className="row">
                                <div className="col-sm-12 mt-2">
                                    {renderResourceItem()}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </>
        )
    }

    return(
        <DashboardWrapper>
            <div className="row">
                <div className="col-6">
                    <DetailBreadcrumb items={[
                        { label: t('Licenses'), to: '/licenses' },
                        { label: title },
                    ]} />
                    <DashboardTitle title={t('License')} isLoading={isLoading}  />
                </div>
                <div className="col-12">
                    {
                        !isLoading && _renderLicenseForm()
                    }
                </div>
            </div>

            <ModalDeleteConfirm 
                show={recoveringLicense}
                title={t('Recover_license')}
                onCloseFunction={setrecoveringLicense}
                onCloseFunctionParameter={false}
                entityTranslation={t('license')}
                buttonLoading={isUpdatingForm}
                onClickFunctionButton={_recoverLicense}
                overrideEntityTranslationAbove={t('Are_you_sure_to_recover_current_f_')}
                buttonText={t('Recover')}
            />

            <CactusModal 
                show={transferLicense}
                title={ t('Transfer_license') }
                onClose={() => {
                    settransferLicense(false)
                }}
                minWidth="500px"
            >
                <div className='row'>
                    {_renderText()}
                        
                    <div className="col-12 d-flex justify-content-between text-right">
                        <button 
                            type="button"
                            onClick={ () => settransferLicense(false) }
                            className="btn btn-cactus--red mt-2"
                            disabled={isUpdatingForm ?? false}
                        >
                            {t('Cancel')}
                        </button>
                        <button 
                            type="submit"
                            onClick={ handleSubmit(_handleOnSubmit) }
                            className="btn btn-cactus mt-2 mr-2"
                            disabled={isUpdatingForm ?? false}
                        >
                            {t('Transfer_license')}
                        </button>
                    </div>
                </div>
            </CactusModal>
        </DashboardWrapper>
    );
};

export default React.memo(LicenseDetail);
